// Generic iOS-style switch — the house replacement for native
// `type: 'checkbox'` inputs (admin pages migrated 2026-07-25 per Nika
// "check all buttons, ui … fix the whole admin area"). Value-driven and
// registry-agnostic; FlagSwitch (components/FlagRow.js) extends this
// with the /admin/flags registry semantics.
//
// CONTRACT: consumers override `onCheck: (el, s) => bool` — deliberately
// `on`-prefixed: v3.14 evaluates ordinary function props ONCE at create
// and freezes the RESULT (el.isChecked became a boolean and every CSS
// factory calling it threw, dropping the whole style layer — verified
// live on /admin/activity-scoring 2026-07-25). `on*` props are the
// framework's handler class: they stay FUNCTIONS on the element, so the
// reactive CSS factories below can dispatch through them per repaint.
// aria-checked rides the attr bag — a top-level quoted dash-key prop
// does not survive `extends` inheritance, attr entries do.
//
// Geometry: track C×(A1 + 2·X) ≈ 44×28 with a ~20px knob — the compact
// Apple ratio. The knob parks left/right via a justify flip; the
// animated cue is the track fill and the knob ink (no ring: the OFF track is
// a visible plate of its own).
export const SwitchInput = {
  tag: 'button',
  type: 'button',
  role: 'switch',
  onCheck: () => false,
  attr: {
    'aria-checked': (el, s) => (el.onCheck && el.onCheck(el, s) ? 'true' : 'false')
  },
  cursor: 'pointer',
  border: 'none',
  width: 'C',
  minWidth: 'C',
  padding: 'X',
  round: 'radiusPill',
  flow: 'x',
  flexShrink: '0',
  align: (el, s) => (el.onCheck && el.onCheck(el, s) ? 'center right' : 'center left'),
  // MONOCHROME, CSS-DRIVEN STATE (admin audit 2026-09-24; DESIGN_LANGUAGE
  // §8.1-8.2). Every state is a CLASS rule keyed on the switch's own
  // `aria-checked` attribute or `:disabled`. There is no inline fill and no
  // inline box-shadow, so the reset's :focus-visible ring shows (an inline
  // box-shadow hid it on 56 switches), and no reactive :hover makes the
  // framework emit !important.
  //
  // The knob paints `currentColor`, so each state sets the switch's
  // `color` for the knob and the `background` for the track:
  //   OFF      track `title.14` (the house tonal step: 1.37 light, 1.46 dark
  //            on the sheet), knob `caption`; hover `title.22`, pressed `title.3`
  //   ON       the ink itself: track `title`, knob `title-reversed` (18.2);
  //            hover `title.8`, pressed `title.7`. The selection wash
  //            (`selected`) cannot mark ON: in light it is one alpha step
  //            from a visible OFF plate.
  //   DISABLED the OFF plate whatever the value, knob `inactive` (sheet /
  //            card: 5.38 / 4.79 light, 4.42 / 3.57 dark; the `disabled`
  //            ink read 2.87 on the dark card) — the knob's side still shows
  //            the value; never opacity (DESIGN_LANGUAGE §8.1).
  // Every step clears Rule 65's 0.02 luminance step in both themes, and the
  // knob clears 3:1 on every step, on the sheet and on a card. Like every
  // choice control, a switch sits on a card or sheet: on the bare dark stage
  // the pressed OFF knob reads 2.78.
  // ORDER MATTERS: the engine doubles the class of a bare `:hover` rule, so
  // it ties with the ON and DISABLED pointer rules; the later rule wins,
  // so the ON block stays after the rest states and DISABLED stays last.
  color: 'caption',
  background: 'title.14',
  transition: 'Z defaultBezier background, Z defaultBezier color',
  ':hover': { background: 'title.22' },
  ':active': { background: 'title.3' },
  '&[aria-checked="true"]': {
    color: 'title-reversed',
    background: 'title',
    ':hover': { background: 'title.8' },
    ':active': { background: 'title.7' }
  },
  ':disabled': {
    color: 'inactive',
    background: 'title.14',
    cursor: 'not-allowed',
    ':hover': { background: 'title.14' },
    ':active': { background: 'title.14' }
  },

  Knob: {
    boxSize: 'A1 A1',
    round: '100%',
    // The switch's ink for its state (see the block above): `caption` OFF,
    // `title-reversed` ON, `inactive` while disabled.
    background: 'currentColor',
    transition: 'Z defaultBezier background',
    flexShrink: '0',
    pointerEvents: 'none'
  }
}
